import { useEffect, useMemo, useRef, useState } from 'react'
import { platformLogo } from '@/lib/platforms'
import { useDispatch } from 'react-redux'
import { createFileRoute } from '@tanstack/react-router'
import { setSidebarOverride } from '@/store/sidebar-override.slice'
import { formatPropertyAddress } from '@/lib/property-address'
import { formatVatTerm } from '@/lib/vat-label'
import { extractErrorMessage } from '@/lib/error-message'
import { normalizeListingPlatform, normalizeListingStatus } from '@/lib/external-listing-status'
import { AlertCircleIcon, ArchiveIcon, ArrowUpDownIcon, CalendarIcon, CheckCircleIcon, ClockIcon, EllipsisVerticalIcon, ExternalLinkIcon, FileTextIcon, FileUpIcon, GlobeIcon, PencilIcon, PlusIcon, RotateCcwIcon, Trash2Icon } from 'lucide-react'

import {
	Accordion, AccordionItem, AccordionTrigger, AccordionContent,
	Avatar, AvatarFallback, AvatarImage,
	Badge,
	BannerButton,
	Button,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
	IconButton,
	PhotoGallery,
	PropertyCardHeader,
	PropertyDetailField, PropertyDetailFieldList,
	PropertyField, PropertyFieldList, PropertyFieldGroup,
	Alert, AlertTitle, AlertDescription,
	ConfirmDialog,
	Spinner,
	Tooltip, TooltipContent, TooltipTrigger,
	TabActionButton,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
	Tabs, TabsList, TabsTrigger,
} from '@rentflowdev/ui'
import { useTranslation } from 'react-i18next'
import {
	useGetMeQuery,
	useGetPropertyQuery, useDeletePropertyMutation, useUpdatePropertyStatusMutation,
	useGetVatRateQuery,
	useListExternalListingsQuery, useListConnectionsQuery,
	usePublishListingMutation, useUnpublishListingMutation, useSyncListingMutation,
	useListSubAccountsQuery,
	useCreateDocumentMutation, useGenerateDocumentUploadUrlMutation, useUpdateDocumentMutation,
	useListRentalsQuery,
} from '@/api'
import type { ExternalListing, Platform, Property, PropertyUtility, RentPeriod } from '@/types'
import { usePageTitleStore } from '@/lib/page-title-store'
import { EditPropertySheet } from '@/components/edit-property-sheet'
import { EditRentTermsDialog } from '@/components/edit-rent-terms-dialog'
import { AssignManagerDialog } from '@/components/assign-manager-dialog'
import { PropertyDocumentsSection } from '@/components/property-documents-section'
import { PropertyPhotosSection } from '@/components/property-photos-section'
import { useNavigate } from '@tanstack/react-router'

export const Route = createFileRoute('/_app/properties/$propertyId')({
	component: PropertyDetailPage,
	// The tab lives in the URL so that returning from a document lands on
	// the tab the document was opened from, not on the default one.
	validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
		tab: typeof search.tab === 'string' ? search.tab : undefined,
	}),
})

const STATUS_VARIANTS: Record<Property['status'], 'success' | 'warning' | 'default' | 'secondary'> = {
	AVAILABLE: 'success',
	RENTED: 'warning',
	DRAFT: 'secondary',
	ARCHIVED: 'default',
}

const PROPERTY_STATUS_KEYS: Record<Property['status'], string> = {
	AVAILABLE: 'properties.statuses.AVAILABLE',
	RENTED: 'properties.statuses.RENTED',
	DRAFT: 'properties.statuses.DRAFT',
	ARCHIVED: 'properties.statuses.ARCHIVED',
}

// ── Characteristics accordion ────────────────────────────

function hasBuildingData(p: Property): boolean {
	return p.yearBuilt != null
		|| p.totalFloors != null
		|| p.buildingType != null
		|| p.passengerElevator != null
		|| p.freightElevator != null
		|| (p.parking?.length ?? 0) > 0
		|| (p.yard?.length    ?? 0) > 0
		|| p.concierge === true
		|| p.chute     === true
		|| p.gas       === true
		|| (p.communications?.length     ?? 0) > 0
		|| (p.transportAccess?.length    ?? 0) > 0
		|| (p.nearbyInfrastructure?.length ?? 0) > 0
}

function hasRulesData(p: Property): boolean {
	return p.maxTenants != null
		|| p.childrenAllowed !== undefined
		|| p.petsAllowed     !== undefined
		|| p.smokingAllowed  !== undefined
}

/** «Карп Борисов» → «КБ». Одна буква читалась как случайная. */
function avatarInitials(name: string): string {
	return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')
}

function snakeToCamel(value: string): string {
	return value.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
}

type TFn = (key: string, opts?: Record<string, unknown>) => string

/** Translates a single stored enum value (e.g. `renovation`, `buildingType`) via its `properties.details.<dictKey>` vocabulary. */
function enumLabel(t: TFn, dictKey: string, value?: string | null): string | undefined {
	if (value == null) return undefined
	// Locale keys are camelCase (e.g. MONOLITH_BRICK → monolithBrick), so a bare
	// toLowerCase() would miss multi-word enum values and render them raw.
	return t(`properties.details.${dictKey}.${snakeToCamel(value.toLowerCase())}`, { defaultValue: value })
}

/** Translates a stored string array (e.g. `furniture`, `parking`) via its `properties.details.<dictKey>` vocabulary and joins for display. */
function listLabel(t: TFn, dictKey: string, values?: string[]): string | undefined {
	if (!values?.length) return undefined
	return values.map((v) => t(`properties.details.${dictKey}.${snakeToCamel(v)}`, { defaultValue: v })).join(', ')
}

/**
 * Словари коммерческих полей ключуются самим значением перечисления
 * (STREET_RETAIL), а не его camelCase-формой — в отличие от жилых, которые
 * писались раньше и по другому соглашению. Поэтому у них свои помощники, а не
 * общий enumLabel с его snakeToCamel.
 */
function rawEnumLabel(t: TFn, dictKey: string, value?: string | null): string | undefined {
	if (value == null) return undefined
	return t(`properties.details.${dictKey}.${value}`, { defaultValue: value })
}

function rawListLabel(t: TFn, dictKey: string, values?: string[]): string | undefined {
	if (!values?.length) return undefined
	return values.map((v) => t(`properties.details.${dictKey}.${v}`, { defaultValue: v })).join(', ')
}

/** Пустое значение остаётся пустым: «нет» и «не указано» — разные ответы. */
function yesNo(t: TFn, value?: boolean | null): string | undefined {
	if (value == null) return undefined
	return value ? t('common.yes') : t('common.no')
}

function PropertyCharacteristics({ property }: { property: Property }) {
	const { t } = useTranslation()
	const isLand = property.type === 'LAND'
	const isRoom = property.type === 'ROOM'
	const isHouse = property.type === 'HOUSE'
	const isGarage = property.type === 'GARAGE'
	// Офис, торговое, складское и коворкинг делят коммерческий набор — тот же
	// признак, что и в мастере.
	const isCommercial = ['OFFICE', 'RETAIL', 'WAREHOUSE', 'COWORKING'].includes(property.type)
	const isCoworking = property.type === 'COWORKING'
	const isRetail = property.type === 'RETAIL'
	const isOffice = property.type === 'OFFICE'

	return (
		<Accordion type="multiple" defaultValue={['about']}>
			<AccordionItem value="about">
				<AccordionTrigger className="text-base font-[500]">
					{t(`properties.card.aboutType.${property.type}`)}
				</AccordionTrigger>
				<AccordionContent>
					{isLand ? (
						<PropertyFieldList>
							<PropertyField label={t('properties.card.plotArea')} value={property.area ? `${property.area} м²` : undefined} />
						</PropertyFieldList>
					) : isRoom ? (
						<PropertyFieldGroup>
							<PropertyFieldList>
								<PropertyField label={t('properties.details.roomLocation')} value={property.roomLocation ? t(`properties.details.roomLocations.${property.roomLocation.toLowerCase()}`, { defaultValue: property.roomLocation }) : undefined} />
								<PropertyField label={t('properties.details.roomsInApartment')} value={property.roomsInApartment} />
								<PropertyField label={t('properties.details.roomHousingType')} value={property.roomHousingType ? t(`properties.details.roomHousingTypes.${property.roomHousingType.toLowerCase()}`, { defaultValue: property.roomHousingType }) : undefined} />
								<PropertyField label={t('properties.details.bedCount')} value={property.bedCount} />
								<PropertyField label={t('properties.details.sleepingPlaces')} value={property.sleepingPlaces} />
								<PropertyField label={t('properties.card.totalArea')} value={property.area ? `${property.area} м²` : undefined} />
								<PropertyField label={t('properties.card.floor')} value={property.totalFloors ? `${property.floor} из ${property.totalFloors}` : property.floor} />
							</PropertyFieldList>
							<PropertyFieldList>
								<PropertyField label={t('properties.card.renovation')} value={enumLabel(t, 'renovationTypes', property.renovation)} />
								<PropertyField label={t('properties.details.eventsAllowed')} value={property.eventsAllowed ? t('common.yes') : t('common.no')} />
								<PropertyField label={t('properties.details.fireplace')} value={property.fireplace ? t('common.yes') : t('common.no')} />
								<PropertyField label={t('properties.card.furniture')} value={listLabel(t, 'furnitureItems', property.furniture)} />
								<PropertyField label={t('properties.card.appliances')} value={listLabel(t, 'applianceItems', property.appliances)} />
								<PropertyField label={t('properties.card.internet')} value={listLabel(t, 'internetItems', property.internet)} />
							</PropertyFieldList>
						</PropertyFieldGroup>
					) : isHouse ? (
						<PropertyFieldGroup>
							<PropertyFieldList>
								<PropertyField label={t('properties.details.houseKind')} value={property.houseKind ? t(`properties.details.houseKinds.${property.houseKind.toLowerCase()}`, { defaultValue: property.houseKind }) : undefined} />
								<PropertyField label={t('properties.card.totalArea')} value={property.area ? `${property.area} м²` : undefined} />
								<PropertyField label={t('properties.details.plotArea')} value={property.plotArea ? `${property.plotArea} сот.` : undefined} />
								<PropertyField label={t('properties.card.rooms')} value={property.rooms} />
								<PropertyField
									label={t('properties.details.bathroomLocation')}
									value={
										property.bathroomLocations?.length
											? property.bathroomLocations
													.map((loc) => t(`properties.details.bathroomLocations.${loc.toLowerCase()}`, { defaultValue: loc }))
													.join(', ')
											: undefined
									}
								/>
							</PropertyFieldList>
							<PropertyFieldList>
								<PropertyField label={t('properties.card.renovation')} value={enumLabel(t, 'renovationTypes', property.renovation)} />
								<PropertyField label={t('properties.details.sauna')} value={property.sauna ? t('common.yes') : t('common.no')} />
								<PropertyField label={t('properties.details.pool')} value={property.pool ? t('common.yes') : t('common.no')} />
								<PropertyField label={t('properties.details.terrace')} value={property.terrace ? t('common.yes') : t('common.no')} />
								<PropertyField label={t('properties.card.furniture')} value={listLabel(t, 'furnitureItems', property.furniture)} />
								<PropertyField label={t('properties.card.appliances')} value={listLabel(t, 'applianceItems', property.appliances)} />
								<PropertyField label={t('properties.card.internet')} value={listLabel(t, 'internetItems', property.internet)} />
							</PropertyFieldList>
						</PropertyFieldGroup>
					) : isGarage ? (
						<PropertyFieldList>
							<PropertyField label={t('properties.details.garageKind')} value={rawEnumLabel(t, 'garageKindOptions', property.garageKind)} />
							<PropertyField label={t('properties.details.garageType')} value={rawEnumLabel(t, 'garageTypeOptions', property.garageType)} />
							<PropertyField label={t('properties.details.parkingSpaceType')} value={rawEnumLabel(t, 'parkingSpaceTypeOptions', property.parkingSpaceType)} />
							<PropertyField label={t('properties.card.totalArea')} value={property.area ? `${property.area} м²` : undefined} />
							<PropertyField label={t('properties.details.security')} value={yesNo(t, property.security)} />
						</PropertyFieldList>
					) : isCommercial ? (
						<PropertyFieldGroup>
							<PropertyFieldList>
								<PropertyField label={t('properties.card.totalArea')} value={property.area ? `${property.area} м²` : undefined} />
								<PropertyField label={t('properties.card.floor')} value={property.multipleFloors ? `${property.floor ?? '—'} ${t('properties.details.multipleFloorsShort')}` : property.floor} />
								<PropertyField label={t('properties.details.officeLayouts')} value={rawListLabel(t, 'officeLayoutOptions', property.officeLayouts)} />
								<PropertyField label={t('properties.details.finishing')} value={rawEnumLabel(t, 'finishingOptions', property.finishing)} />
								<PropertyField label={t('properties.details.ceilingHeight')} value={property.ceilingHeight} />
								<PropertyField label={t('properties.details.powerCapacity')} value={property.powerCapacity} />
								<PropertyField label={t('properties.details.powerCapacityExpandable')} value={yesNo(t, property.powerCapacityExpandable)} />
								<PropertyField label={t('properties.details.partialRent')} value={yesNo(t, property.partialRent)} />
								<PropertyField label={t('properties.details.onlineViewing')} value={yesNo(t, property.onlineViewing)} />
							</PropertyFieldList>
							<PropertyFieldList>
								{/* Показываем ровно то, что мастер спрашивает у этого типа:
								    иначе склад и коворкинг печатали «Тип помещения: —» —
								    вопрос, который им никто не задавал. */}
								{isRetail && <PropertyField label={t('properties.details.retailPlacement')} value={rawEnumLabel(t, 'retailPlacementOptions', property.retailPlacement)} />}
								{!isOffice && (
									<>
										<PropertyField label={t('properties.details.entrance')} value={rawEnumLabel(t, 'entranceOptions', property.entrance)} />
										<PropertyField label={t('properties.details.separateEntrance')} value={yesNo(t, property.separateEntrance)} />
										<PropertyField label={t('properties.details.commercialHeating')} value={rawEnumLabel(t, 'commercialHeatingOptions', property.commercialHeating)} />
									</>
								)}
								<PropertyField label={t('properties.details.additionalCategories')} value={rawListLabel(t, 'additionalCategoryOptions', property.additionalCategories)} />
								{isCoworking && <PropertyField label={t('properties.details.seatsCount')} value={property.seatsCount} />}
								{isCoworking && <PropertyField label={t('properties.details.workplaceStatus')} value={rawEnumLabel(t, 'workplaceStatusOptions', property.workplaceStatus)} />}
								{isCoworking && <PropertyField label={t('properties.details.amenitiesInclusion')} value={rawEnumLabel(t, 'amenitiesInclusionOptions', property.amenitiesInclusion)} />}
							</PropertyFieldList>
						</PropertyFieldGroup>
					) : (
						<PropertyFieldGroup>
							<PropertyFieldList>
								<PropertyField label={t('properties.card.rooms')} value={property.rooms} />
								<PropertyField label={t('properties.card.totalArea')} value={property.area ? `${property.area} м²` : undefined} />
								<PropertyField label={t('properties.card.livingArea')} value={property.livingArea ? `${property.livingArea} м²` : undefined} />
								<PropertyField label={t('properties.card.floor')} value={property.totalFloors ? `${property.floor} из ${property.totalFloors}` : property.floor} />
								<PropertyField label={t('properties.card.balcony')} value={property.balcony ? t('common.yes') : t('common.no')} />
								<PropertyField label={t('properties.card.bathroom')} value={enumLabel(t, 'bathroomTypes', property.bathroom)} />
							</PropertyFieldList>
							<PropertyFieldList>
								<PropertyField label={t('properties.card.renovation')} value={enumLabel(t, 'renovationTypes', property.renovation)} />
								<PropertyField label={t('properties.card.furniture')} value={listLabel(t, 'furnitureItems', property.furniture)} />
								<PropertyField label={t('properties.card.appliances')} value={listLabel(t, 'applianceItems', property.appliances)} />
								<PropertyField label={t('properties.card.internet')} value={listLabel(t, 'internetItems', property.internet)} />
							</PropertyFieldList>
						</PropertyFieldGroup>
					)}
				</AccordionContent>
			</AccordionItem>
			{!isLand && !isCommercial && !isGarage && <AccordionItem value="building">
				<AccordionTrigger className="text-base font-[500]">{t('properties.card.aboutBuilding')}</AccordionTrigger>
				<AccordionContent>
					{hasBuildingData(property) ? (
						<PropertyFieldGroup>
							<PropertyFieldList>
								<PropertyField label={t('properties.card.yearBuilt')}     value={property.yearBuilt} />
								<PropertyField label={t('properties.card.totalFloors')}   value={property.totalFloors} />
								<PropertyField label={t('properties.card.buildingType')}  value={enumLabel(t, 'buildingTypes', property.buildingType)} />
								<PropertyField label={t('properties.card.passengerElevator')} value={property.passengerElevator} />
								<PropertyField label={t('properties.card.freightElevator')}   value={property.freightElevator} />
							</PropertyFieldList>
							<PropertyFieldList>
								<PropertyField label={t('properties.card.concierge')} value={property.concierge ? t('common.yes') : t('common.no')} />
								<PropertyField label={t('properties.card.chute')}     value={property.chute     ? t('common.yes') : t('common.no')} />
								<PropertyField label={t('properties.card.gas')}       value={property.gas       ? t('common.yes') : t('common.no')} />
								<PropertyField label={t('properties.card.parking')}   value={listLabel(t, 'parkingTypes', property.parking)} />
								<PropertyField label={t('properties.card.yard')}      value={listLabel(t, 'yardItems', property.yard)} />
								{isHouse && <PropertyField label={t('properties.details.communications')} value={listLabel(t, 'communicationsItems', property.communications)} />}
								{isHouse && <PropertyField label={t('properties.details.transportAccess')} value={listLabel(t, 'transportAccessItems', property.transportAccess)} />}
								{isHouse && <PropertyField label={t('properties.details.nearbyInfrastructure')} value={listLabel(t, 'nearbyInfrastructureItems', property.nearbyInfrastructure)} />}
							</PropertyFieldList>
						</PropertyFieldGroup>
					) : (
						<p className="text-sm text-(--color-muted-foreground)">{t('properties.card.noData')}</p>
					)}
				</AccordionContent>
			</AccordionItem>}
			{!isLand && !isCommercial && !isGarage && <AccordionItem value="rules">
				<AccordionTrigger className="text-base font-[500]">{t('properties.card.rules')}</AccordionTrigger>
				<AccordionContent>
					{hasRulesData(property) ? (
						<PropertyFieldList>
							<PropertyField label={t('properties.card.maxTenants')}      value={property.maxTenants} />
							<PropertyField label={t('properties.card.childrenAllowed')} value={property.childrenAllowed === undefined ? undefined : property.childrenAllowed ? t('common.yes') : t('common.no')} />
							<PropertyField label={t('properties.card.petsAllowed')}     value={property.petsAllowed     === undefined ? undefined : property.petsAllowed     ? t('common.yes') : t('common.no')} />
							<PropertyField label={t('properties.card.smokingAllowed')}  value={property.smokingAllowed  === undefined ? undefined : property.smokingAllowed  ? t('common.yes') : t('common.no')} />
						</PropertyFieldList>
					) : (
						<p className="text-sm text-(--color-muted-foreground)">{t('properties.card.noData')}</p>
					)}
				</AccordionContent>
			</AccordionItem>}
			<AccordionItem value="location">
				<AccordionTrigger className="text-base font-[500]">{t('properties.card.location')}</AccordionTrigger>
				<AccordionContent>
					<p className="text-sm text-(--color-foreground)">
						{formatPropertyAddress(property.city, property.address)}
					</p>
				</AccordionContent>
			</AccordionItem>
			<AccordionItem value="contacts">
				<AccordionTrigger className="text-base font-[500]">{t('properties.card.contacts')}</AccordionTrigger>
				<AccordionContent>
					{property.manager ? (
						<PropertyField label={t('properties.card.manager')} value={property.manager.name} />
					) : (
						<p className="text-sm text-(--color-muted-foreground)">{t('properties.card.noData')}</p>
					)}
				</AccordionContent>
			</AccordionItem>
			<AccordionItem value="description">
				<AccordionTrigger className="text-base font-[500]">{t('properties.card.description')}</AccordionTrigger>
				<AccordionContent>
					<div className="flex flex-col gap-3">
						{property.description ? (
							<p className="whitespace-pre-wrap text-sm text-(--color-foreground)">{property.description}</p>
						) : (
							<p className="text-sm text-(--color-muted-foreground)">{t('properties.card.noData')}</p>
						)}
						{/* Ссылку на видео мастер спрашивал, сервер хранил, а карточка не
						    показывала — единственное поле объекта, которое было некуда
						    посмотреть после сохранения. */}
						{property.videoUrl && (
							<a
								href={property.videoUrl}
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 self-start text-sm text-(--color-info) hover:underline"
							>
								<ExternalLinkIcon className="size-3.5" />
								{t('properties.photos.video')}
							</a>
						)}
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
}

// ── Rent terms accordion ─────────────────────────────────

// Keyed by `RentPeriod` so the compiler flags drift when the enum changes.
const PERIOD_RU: Record<RentPeriod, string> = {
	WEEKLY: 'в неделю',
	MONTHLY: 'в месяц',
	QUARTERLY: 'в квартал',
	YEARLY: 'в год',
}

function currencySymbol(c?: string): string {
	return c === 'USD' ? '$' : c === 'EUR' ? '€' : '₽'
}

function formatRentAmount(property: Property): string {
	const rt = property.rentTerms
	if (!rt?.rentAmount) return '—'
	const amount = Number(rt.rentAmount).toLocaleString('ru-RU')
	const period = rt.rentPeriod ? ` ${PERIOD_RU[rt.rentPeriod] ?? rt.rentPeriod}` : ''
	return `${amount} ${currencySymbol(rt.rentCurrency)}${period}`
}

function formatDeposit(property: Property): string | undefined {
	const rt = property.rentTerms
	if (!rt?.depositAmount) return undefined
	return `${Number(rt.depositAmount).toLocaleString('ru-RU')} ${currencySymbol(rt.depositCurrency)}`
}

function formatPenalty(property: Property): string {
	const rt = property.rentTerms
	if (!rt?.penaltyRate) return '—'
	const grace = rt.penaltyGraceDays ? `, льготный период ${rt.penaltyGraceDays} дня` : ''
	return `${rt.penaltyRate}% в день${grace}`
}

function formatUtility(u: PropertyUtility | undefined, t: (key: string) => string): string {
	if (!u?.calculation) return '—'
	const calc = t(`properties.card.rentTerms.calc.${u.calculation}`)
	const paidBy = u.paidBy ? `, ${t(`properties.card.rentTerms.paidBy.${u.paidBy}`)}` : ''
	return `${calc}${paidBy}`
}

function PropertyRentTerms({ property }: { property: Property }) {
	const { t } = useTranslation()
	const rt = 'properties.card.rentTerms'

	// The stored rate is a snapshot: changing the tax regime deliberately does
	// not rewrite saved terms. Without saying so, the difference reads as the
	// rate having failed to update.
	const { data: current } = useGetVatRateQuery(
		{ type: property.type, housingType: property.housingType },
		{ skip: !property.rentTerms },
	)
	const storedPercent = property.rentTerms?.vatPercent
	const isStale =
		current?.kind === 'rate' &&
		storedPercent != null &&
		Number(storedPercent) !== current.percent

	const getUtility = (type: string) => property.utilities?.find((u) => u.type === type)

	return (
		<Accordion type="multiple" defaultValue={['basic', 'utilities']}>
			<AccordionItem value="basic">
				<AccordionTrigger className="text-base font-[500]">{t(`${rt}.basicTerms`)}</AccordionTrigger>
				<AccordionContent>
					<PropertyDetailFieldList>
						<PropertyDetailField label={t(`${rt}.rentAmount`)} value={formatRentAmount(property)} />
						<PropertyDetailField label={t(`${rt}.vat`)} value={property.rentTerms ? formatVatTerm(property.rentTerms, t) : undefined} />
						{isStale && (
							<p className="text-xs text-(--color-muted-foreground)">
								{t(`${rt}.vatStale`, { percent: current.percent })}
							</p>
						)}
						<PropertyDetailField label={t(`${rt}.deposit`)} value={formatDeposit(property)} />
						<PropertyDetailField label={t(`${rt}.penalty`)} value={formatPenalty(property)} />
					</PropertyDetailFieldList>
				</AccordionContent>
			</AccordionItem>
			<AccordionItem value="utilities">
				<AccordionTrigger className="text-base font-[500]">{t(`${rt}.utilities`)}</AccordionTrigger>
				<AccordionContent>
					<div className="flex flex-col gap-3 text-sm">
						{(['ELECTRICITY', 'WATER', 'HEATING', 'WASTE'] as const).map((type) => {
							const u = getUtility(type)
							return (
								<div key={type} className="flex items-center gap-5">
									<span className="w-[195px] shrink-0 leading-5 text-(--color-muted-foreground)">
										{t(`${rt}.${type.toLowerCase()}`)}
									</span>
									<span className="flex-1 leading-5 text-(--color-foreground)">
										{formatUtility(u, t)}
									</span>
								</div>
							)
						})}
					</div>
				</AccordionContent>
			</AccordionItem>
			<AccordionItem value="services">
				<AccordionTrigger className="text-base font-[500]">{t(`${rt}.additionalServices`)}</AccordionTrigger>
				<AccordionContent>
					{property.additionalServices?.length ? (
						<PropertyDetailFieldList>
							{property.additionalServices.map((s) => (
								<PropertyDetailField
									key={s.id}
									label={`${s.name}:`}
									/* The saved snapshot, through the same helper the rent
									   terms use — «(вкл. НДС)» named the old flag, which said
									   nothing about the rate or its legal ground. */
									value={[
										`${Number(s.pricePerUnit).toLocaleString('ru-RU')} ${currencySymbol(s.currency)} × ${s.quantity} ${s.unit}`,
										formatVatTerm(s, t),
									]
										.filter(Boolean)
										.join(' · ')}
								/>
							))}
						</PropertyDetailFieldList>
					) : (
						<p className="text-sm text-(--color-muted-foreground)">{t('properties.card.noData')}</p>
					)}
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
}

// ── Rent sidebar (banner cards) ──────────────────────────

function PropertyRentSidebar({ property }: { property: Property }) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const rt = 'properties.card.rentTerms'
	const isRented = property.status === 'RENTED'

	// Filter server-side by propertyId: scanning an account-wide page of rentals
	// client-side breaks as soon as the account has more rentals than one page.
	// "Current" is ACTIVE + CLOSING, the same pair the rentals section uses — a
	// rental being wound down is still the one in force.
	const { data: currentData }   = useListRentalsQuery({ statuses: ['ACTIVE', 'CLOSING'], propertyId: property.id, limit: 1 })
	const { data: upcomingData }  = useListRentalsQuery({ status: 'UPCOMING',  propertyId: property.id, limit: 1 })
	const { data: completedData } = useListRentalsQuery({ status: 'COMPLETED', propertyId: property.id, limit: 1 })
	const currentRental = currentData?.rentals[0]
	const hasUpcoming  = (upcomingData?.total  ?? 0) > 0 || (upcomingData?.rentals.length  ?? 0) > 0
	const hasCompleted = (completedData?.total ?? 0) > 0 || (completedData?.rentals.length ?? 0) > 0

	return (
		<div className="flex w-[246px] flex-col gap-3">
			{isRented && (
				<BannerButton
					icon={<PencilIcon className="size-5" />}
					title={t(`${rt}.currentRent`)}
					className="w-full"
					disabled={!currentRental}
					onClick={() => {
						if (currentRental) void navigate({ to: '/rent/$rentalId', params: { rentalId: currentRental.id } })
					}}
				/>
			)}
			<BannerButton
				icon={<CalendarIcon className="size-5" />}
				title={t(`${rt}.upcomingRent`)}
				className="w-full"
				disabled={!hasUpcoming}
				onClick={() => void navigate({ to: '/rent/upcoming', search: { propertyId: property.id } })}
			/>
			<BannerButton
				icon={<ClockIcon className="size-5" />}
				title={t(`${rt}.rentHistory`)}
				className="w-full"
				disabled={!hasCompleted}
				onClick={() => void navigate({ to: '/rent/completed', search: { propertyId: property.id } })}
			/>
		</div>
	)
}

// ── Publications table ───────────────────────────────────

const STUDIO_URL = import.meta.env.VITE_STUDIO_URL ?? 'https://studio.rentflow.tech'


const PUB_STATUS_VARIANTS: Record<string, 'success' | 'destructive' | 'warning' | 'secondary'> = {
	PUBLISHED: 'success',
	UNPUBLISHED: 'secondary',
	MODERATION: 'warning',
	PAUSED: 'warning',
	PENDING: 'secondary',
	ERROR: 'destructive',
}

const ALL_PLATFORMS: Platform[] = ['AVITO', 'CIAN', 'SITE']


function buildListingUrl(platform: Platform, externalId?: string): string | undefined {
	if (!externalId) return undefined
	if (platform === 'AVITO') return `https://www.avito.ru/items/${externalId}`
	if (platform === 'CIAN')  return `https://www.cian.ru/rent/flat/${externalId}/`
	// SITE stores the published subdomain slug as externalId.
	if (platform === 'SITE')  return `https://${externalId}.rentflow.tech`
	return undefined
}

function PropertyPublications({ property }: { property: Property }) {
	const { t } = useTranslation()
	const pub = 'properties.card.publications'

	const { data: listingsData, isLoading } = useListExternalListingsQuery({ propertyId: property.id })
	const { data: connectionsData } = useListConnectionsQuery()
	const [publishListing, { isLoading: isPublishing }] = usePublishListingMutation()
	const [unpublishListing] = useUnpublishListingMutation()
	const [syncListing, { isLoading: isSyncing, originalArgs: syncingListingId }] = useSyncListingMutation()
	const [actionError, setActionError] = useState<{ title: string; description?: string } | null>(null)

	// Narrowed row type: listing platforms are only AVITO/CIAN/SITE (web
	// `Platform`); the api union also carries TELEGRAM which never appears in
	// external listings.
	const listings = (listingsData?.listings ?? []).map(
		(l): Omit<ExternalListing, 'platform'> & { platform: Platform } => ({
			...l,
			platform: normalizeListingPlatform(l.platform as unknown as string),
			status: normalizeListingStatus(l.status as unknown as string),
		}),
	)
	const connectedPlatforms = useMemo(() => new Set(
		(connectionsData?.connections ?? []).filter((c) => c.isActive).map((c) => c.platform),
	), [connectionsData])

	const availablePlatforms = useMemo(() => {
		const used = new Set(listings.map((l) => l.platform))
		return ALL_PLATFORMS.filter((p) => !used.has(p))
	}, [listings])

	async function handlePublish(platform: Platform) {
		setActionError(null)
		if (platform === 'SITE') {
			window.open(`${STUDIO_URL}/property/${property.id}`, '_blank', 'noopener,noreferrer')
			return
		}
		try {
			await publishListing({ propertyId: property.id, platform }).unwrap()
		} catch (err) {
			setActionError({ title: t(`${pub}.errors.publish`), description: extractErrorMessage(err) })
		}
	}

	async function handleUnpublish(platform: Platform) {
		setActionError(null)
		try {
			await unpublishListing({ propertyId: property.id, platform }).unwrap()
		} catch (err) {
			setActionError({ title: t(`${pub}.errors.unpublish`), description: extractErrorMessage(err) })
		}
	}

	async function handleSync(listingId: string) {
		setActionError(null)
		try {
			await syncListing(listingId).unwrap()
		} catch (err) {
			setActionError({ title: t(`${pub}.errors.sync`), description: extractErrorMessage(err) })
		}
	}

	if (isLoading) {
		return (
			<div className="flex flex-1 items-center justify-center py-16">
				<Spinner />
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-3 p-4">
			{actionError && (
				<Alert variant="destructive">
					<AlertCircleIcon className="size-4" />
					<AlertTitle>{actionError.title}</AlertTitle>
					{actionError.description && <AlertDescription>{actionError.description}</AlertDescription>}
				</Alert>
			)}
			{listings.length > 0 && (
				<div className="overflow-hidden rounded-lg border border-(--color-border)">
					<Table>
						<TableHeader>
							<TableRow className="hover:bg-transparent">
								<TableHead className="text-(--color-muted-foreground)">
									<Button
										variant="ghost"
										iconRight={<ArrowUpDownIcon className="size-3.5" />}
										className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
									>
										{t(`${pub}.platform`)}
									</Button>
								</TableHead>
								<TableHead className="w-24 text-(--color-muted-foreground) font-[500]">{t(`${pub}.favorites`)}</TableHead>
								<TableHead className="w-24 text-(--color-muted-foreground) font-[500]">{t(`${pub}.views`)}</TableHead>
								<TableHead className="w-48 text-(--color-muted-foreground) font-[500]">{t(`${pub}.status`)}</TableHead>
								<TableHead className="w-48 text-(--color-muted-foreground) font-[500]">{t(`${pub}.lastSync`)}</TableHead>
								<TableHead className="w-64" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{listings.map((l) => {
								const logo = platformLogo(l.platform)
								const listingUrl = buildListingUrl(l.platform, l.externalId)
								const platformNode = (
									<div className="flex items-center gap-3">
										{logo
											? <img src={logo} alt={l.platform} className="size-12 shrink-0 rounded-lg object-cover" />
											: (
												<div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-(--color-muted)">
													<GlobeIcon className="size-6 text-(--color-muted-foreground)" />
												</div>
											)
										}
										<span className="flex flex-col">
											<span className="text-sm text-(--color-foreground)">
												{t(`platforms.${l.platform}`, { defaultValue: l.platform })}
											</span>
											{listingUrl && (
												<span className="inline-flex items-center gap-1 text-xs text-(--color-info)">
													<ExternalLinkIcon className="size-3" />
													{t(`${pub}.openListing`)}
												</span>
											)}
										</span>
									</div>
								)
								return (
								<TableRow key={l.id} className="h-[76px]">
									<TableCell className="px-4">
										{listingUrl
											? <a href={listingUrl} target="_blank" rel="noreferrer" className="inline-flex hover:underline">{platformNode}</a>
											: platformNode}
									</TableCell>
									<TableCell className="w-24 px-4 text-sm text-(--color-foreground)">{l.favoritesCount ?? '—'}</TableCell>
									<TableCell className="w-24 px-4 text-sm text-(--color-foreground)">{l.viewsCount ?? '—'}</TableCell>
									<TableCell className="w-48 px-4">
										<Badge variant={PUB_STATUS_VARIANTS[l.status] ?? 'secondary'} size="sm">
											{t(`${pub}.statuses.${l.status}`)}
										</Badge>
										{l.status === 'ERROR' && l.error && (
											<p className="mt-1 text-xs text-(--color-destructive)">{l.error}</p>
										)}
									</TableCell>
									<TableCell className="w-48 px-4 text-sm text-(--color-muted-foreground)">
										{l.lastSyncedAt
											? new Date(l.lastSyncedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
											: '—'}
									</TableCell>
									<TableCell className="w-64 px-4">
										{/* «Редактировать сайт» раньше стояло вместо всего
										    остального, поэтому у сайта не было ни паузы, ни
										    возобновления: приостановить его можно было только
										    в редакторе, а тот про нашу запись публикации не
										    знает — статус здесь так и оставался «Опубликовано».
										    Теперь это ещё одна кнопка рядом, а не замена им. */}
										<div className="flex flex-wrap gap-2">
											{l.platform === 'SITE' && (
												<Button
													variant="secondary"
													size="sm"
													onClick={() => window.open(`${STUDIO_URL}/property/${property.id}`, '_blank', 'noopener,noreferrer')}
												>
													{t(`${pub}.editInStudio`)}
												</Button>
											)}
											{l.status === 'UNPUBLISHED' ? (
												<Button
													size="sm"
													onClick={() => void handlePublish(l.platform)}
												>
													{t(`${pub}.configure`)}
												</Button>
											) : l.status === 'PUBLISHED' || l.status === 'MODERATION' ? (
												<>
													{/* Синхронизировать нечего: сайт наш, он читает
													    объект напрямую. */}
													{l.platform !== 'SITE' && (
														<Button
															variant="secondary"
															size="sm"
															onClick={() => void handleSync(l.id)}
															disabled={isSyncing && syncingListingId === l.id}
														>
															{t(`${pub}.sync`)}
														</Button>
													)}
													<Button
														variant="secondary"
														size="sm"
														onClick={() => void handleUnpublish(l.platform)}
													>
														{t(`${pub}.pause`)}
													</Button>
												</>
											) : (
												<Button
													variant="secondary"
													size="sm"
													onClick={() => void handlePublish(l.platform)}
												>
													{t(`${pub}.resume`)}
												</Button>
											)}
										</div>
									</TableCell>
								</TableRow>
								)
							})}
						</TableBody>
					</Table>
				</div>
			)}

			{availablePlatforms.length > 0 && (
				<div className={listings.length > 0 ? 'mt-4' : ''}>
					<p className="mb-3 text-sm font-[500] text-(--color-foreground)">{t(`${pub}.addTitle`)}</p>
					<div className="flex gap-2">
						{availablePlatforms.map((platform) => {
							const needsConnection = platform === 'AVITO' || platform === 'CIAN'
							const isConnected = connectedPlatforms.has(platform)
							const disabled = isPublishing || (needsConnection && !isConnected)
							const button = (
								<Button
									key={platform}
									variant="outline"
									size="sm"
									iconLeft={<PlusIcon className="size-4" />}
									disabled={disabled}
									onClick={() => void handlePublish(platform)}
								>
									{t(`platforms.${platform}`)}
								</Button>
							)
							if (needsConnection && !isConnected) {
								return (
									<Tooltip key={platform}>
										<TooltipTrigger render={<span className="inline-block" />}>{button}</TooltipTrigger>
										<TooltipContent>{t(`${pub}.connectFirst`, { platform: t(`platforms.${platform}`) })}</TooltipContent>
									</Tooltip>
								)
							}
							return button
						})}
					</div>
				</div>
			)}

			{listings.length === 0 && availablePlatforms.length === 0 && (
				<div className="flex flex-1 items-center justify-center py-16">
					<p className="text-sm text-(--color-muted-foreground)">{t('properties.card.noData')}</p>
				</div>
			)}
		</div>
	)
}

// ── Page ─────────────────────────────────────────────────

function PropertyDetailPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { propertyId } = Route.useParams()
	const { data: property, isLoading } = useGetPropertyQuery(propertyId)
	const [deleteProperty] = useDeletePropertyMutation()
	const [updateStatus] = useUpdatePropertyStatusMutation()
	const { tab: tabParam } = Route.useSearch()
	const tab = tabParam ?? 'characteristics'
	const setTab = (next: string) => {
		void navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, tab: next }), replace: true })
	}
	const [editSheetOpen, setEditSheetOpen] = useState(false)
	const [editRentTermsOpen, setEditRentTermsOpen] = useState(false)
	const [assignManagerOpen, setAssignManagerOpen] = useState(false)
	const [deleteOpen, setDeleteOpen] = useState(false)
	const [docMenuOpen, setDocMenuOpen] = useState(false)
	const [selectTemplateOpen, setSelectTemplateOpen] = useState(false)
	const [pubMenuOpen, setPubMenuOpen] = useState(false)
	const [quickPublishError, setQuickPublishError] = useState<{ title: string; description?: string } | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [createDocument] = useCreateDocumentMutation()
	const [generateUploadUrl] = useGenerateDocumentUploadUrlMutation()
	const [updateDocument] = useUpdateDocumentMutation()
	const [publishListing] = usePublishListingMutation()
	const { data: me } = useGetMeQuery()
	const { data: subAccountsData } = useListSubAccountsQuery({ status: 'ACTIVE' })
	const { data: listingsData } = useListExternalListingsQuery({ propertyId })
	const { data: connectionsData } = useListConnectionsQuery()
	const setPageTitle = usePageTitleStore((s) => s.setPageTitle)
	const dispatch = useDispatch()

	// A draft is an unfinished record — it has no business being advertised
	// anywhere, so the whole publications surface is withheld until it is
	// activated. Handles the proto-string form too, as elsewhere on this page.
	const isDraft = property?.status === 'DRAFT' || property?.status?.endsWith('_DRAFT') === true

	const quickPublishPlatforms = useMemo(() => {
		if (isDraft) return []
		const published = new Set(
			(listingsData?.listings ?? []).map((l) => normalizeListingPlatform(l.platform as unknown as string)),
		)
		const connected = new Set(
			(connectionsData?.connections ?? []).filter((c) => c.isActive).map((c) => c.platform),
		)
		return ALL_PLATFORMS.filter((p) => !published.has(p) && (p === 'SITE' || connected.has(p)))
	}, [listingsData, connectionsData, isDraft])

	// Derived rather than reset in an effect: the trigger disappears when a
	// property is sent back to draft, and a stale `tab` would otherwise leave
	// the body blank with nothing selected.
	const activeTab = isDraft && tab === 'publications' ? 'characteristics' : tab

	useEffect(() => {
		if (property) {
			const title = property.rooms
				? `${t('properties.roomsSuffix', { rooms: property.rooms })} ${t(`properties.types.${property.type}`).toLowerCase()} ${formatPropertyAddress(property.city, property.address)}`
				: `${t(`properties.types.${property.type}`)} ${formatPropertyAddress(property.city, property.address)}`
			setPageTitle(title)
		}
		return () => setPageTitle(null)
	}, [property, t, setPageTitle])

	useEffect(() => {
		if (!property) return
		const childPath =
			property.status === 'DRAFT'    ? '/properties/drafts' :
			property.status === 'ARCHIVED' ? '/properties/archive' :
			                                 '/properties/registry'
		dispatch(setSidebarOverride(childPath))
		return () => { dispatch(setSidebarOverride(null)) }
	}, [property, dispatch])

	if (isLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner />
			</div>
		)
	}

	if (!property) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-sm text-(--color-muted-foreground)">{t('properties.card.notFound')}</p>
			</div>
		)
	}

	function handlePropertyUploadFromComputer() {
		setDocMenuOpen(false)
		fileInputRef.current?.click()
	}

	async function handleQuickPublish(platform: Platform) {
		setPubMenuOpen(false)
		setQuickPublishError(null)
		if (!property) return
		if (platform === 'SITE') {
			window.open(`${STUDIO_URL}/property/${property.id}`, '_blank', 'noopener,noreferrer')
			return
		}
		try {
			await publishListing({ propertyId: property.id, platform }).unwrap()
		} catch (err) {
			setQuickPublishError({
				title:       t('properties.card.publications.errors.publish'),
				description: extractErrorMessage(err),
			})
		}
	}

	async function handlePropertyFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		e.target.value = ''
		if (!file) return
		try {
			const { uploadUrl, publicUrl } = await generateUploadUrl({
				contentType: file.type,
				fileName:    file.name,
			}).unwrap()
			await new Promise<void>((resolve, reject) => {
				const xhr = new XMLHttpRequest()
				xhr.open('PUT', uploadUrl)
				xhr.setRequestHeader('Content-Type', file.type)
				xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)))
				xhr.onerror = () => reject(new Error('Network error'))
				xhr.send(file)
			})
			if (!property) return
			const doc = await createDocument({
				type:       'OTHER',
				title:      file.name,
				entityType: 'PROPERTY',
				entityId:   property.id,
			}).unwrap()
			if (doc?.id) {
				await updateDocument({ id: doc.id, body: { fileUrl: publicUrl } }).unwrap()
			}
		} catch (err) {
			console.error('upload failed:', err)
		}
	}

	const title = property.rooms
		? `${t('properties.roomsSuffix', { rooms: property.rooms })} ${t(`properties.types.${property.type}`).toLowerCase()}`
		: t(`properties.types.${property.type}`)

	return (
		<div className="flex h-full flex-col overflow-hidden">
			<PropertyCardHeader
				title={title}
				subtitle={formatPropertyAddress(property.city, property.address)}
				badge={
					<Badge variant={STATUS_VARIANTS[property.status]} size="md">
						{t(PROPERTY_STATUS_KEYS[property.status])}
					</Badge>
				}
				actions={
					<DropdownMenu>
						<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label="Actions" />}>
							<EllipsisVerticalIcon className="size-4" />
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="min-w-40">
							<DropdownMenuItem onClick={() => setEditSheetOpen(true)}>
								<PencilIcon className="size-4" />
								{t('properties.edit')}
							</DropdownMenuItem>
							{property.status === 'DRAFT' && (
								<DropdownMenuItem
									onClick={() => updateStatus({ id: property.id, body: { status: 'AVAILABLE' } })}
								>
									<CheckCircleIcon className="size-4" />
									{t('properties.activate')}
								</DropdownMenuItem>
							)}
							{property.status === 'ARCHIVED' ? (
								<DropdownMenuItem
									onClick={() => updateStatus({ id: property.id, body: { status: 'AVAILABLE' } })}
								>
									<RotateCcwIcon className="size-4" />
									{t('properties.unarchive')}
								</DropdownMenuItem>
							) : (
								<DropdownMenuItem
									onClick={() => updateStatus({ id: property.id, body: { status: 'ARCHIVED' } })}
								>
									<ArchiveIcon className="size-4" />
									{t('properties.archive')}
								</DropdownMenuItem>
							)}
							<DropdownMenuSeparator />
							<DropdownMenuItem
								variant="destructive"
								onClick={() => setDeleteOpen(true)}
							>
								<Trash2Icon className="size-4" />
								{t('properties.delete')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				}
			/>

			{quickPublishError && (
				<div className="px-4 pb-2">
					<Alert variant="destructive">
						<AlertCircleIcon className="size-4" />
						<AlertTitle>{quickPublishError.title}</AlertTitle>
						{quickPublishError.description && <AlertDescription>{quickPublishError.description}</AlertDescription>}
					</Alert>
				</div>
			)}

			{/* Tabs */}
			<div className="px-4">
				<Tabs value={activeTab} onValueChange={setTab}>
					<TabsList variant="line">
						{([
							{ value: 'characteristics', i18n: 'characteristics', icon: 'pencil', onActionClick: () => setEditSheetOpen(true) },
							{ value: 'rent-terms',      i18n: 'rentTerms',       icon: 'pencil', onActionClick: () => setEditRentTermsOpen(true) },
							{ value: 'publications',    i18n: 'publications',    icon: 'plus',   onActionClick: undefined, showAction: quickPublishPlatforms.length > 0 },
							{ value: 'documents',       i18n: 'documents',       icon: 'plus',   onActionClick: undefined, showAction: false as const },
							{ value: 'manager',         i18n: 'manager',         icon: 'pencil', onActionClick: () => setAssignManagerOpen(true) },
						] as const)
							.filter(({ value }) => !(isDraft && value === 'publications'))
							.map(({ value, i18n, icon, onActionClick, ...rest }) => {
							const showAction = 'showAction' in rest ? (rest as { showAction?: boolean }).showAction !== false : true
							return (
								<TabsTrigger key={value} variant="line" value={value} className="group/tab">
									{t(`properties.card.tabs.${i18n}`)}
									{onActionClick ? (
										<TabActionButton onClick={onActionClick}>
											<PencilIcon className="size-3.5" />
										</TabActionButton>
									) : value === 'documents' ? (
										<DropdownMenu open={docMenuOpen} onOpenChange={setDocMenuOpen}>
											<DropdownMenuTrigger
												// TabActionButton renders a <span role="button">, not a native
												// <button>, so Base UI's default nativeButton={true} warns. Same
												// opt-out as packages/ui pagination.tsx.
												nativeButton={false}
												render={
													<TabActionButton ariaLabel={t('properties.card.documents.actions.menu')}>
														<PlusIcon className="size-3.5" />
													</TabActionButton>
												}
											/>
											<DropdownMenuContent align="start" className="w-auto min-w-48">
												<DropdownMenuItem onClick={() => { setDocMenuOpen(false); setSelectTemplateOpen(true) }}>
													<FileTextIcon />
													{t('properties.card.documents.actions.createFromTemplate')}
												</DropdownMenuItem>
												<DropdownMenuItem onClick={handlePropertyUploadFromComputer}>
													<FileUpIcon />
													{t('properties.card.documents.actions.uploadFromComputer')}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									) : value === 'publications' && showAction ? (
										<DropdownMenu open={pubMenuOpen} onOpenChange={setPubMenuOpen}>
											<DropdownMenuTrigger
												// TabActionButton renders a <span role="button">, not a native
												// <button>, so Base UI's default nativeButton={true} warns. Same
												// opt-out as packages/ui pagination.tsx.
												nativeButton={false}
												render={
													<TabActionButton ariaLabel={t('properties.card.publications.addTitle')}>
														<PlusIcon className="size-3.5" />
													</TabActionButton>
												}
											/>
											<DropdownMenuContent align="start" className="w-auto min-w-48">
												{quickPublishPlatforms.map((platform) => (
													<DropdownMenuItem key={platform} onClick={() => void handleQuickPublish(platform)}>
														<PlusIcon />
														{t(`platforms.${platform}`)}
													</DropdownMenuItem>
												))}
											</DropdownMenuContent>
										</DropdownMenu>
									) : showAction ? (
										<span className="flex size-7 items-center justify-center rounded-(--radius-sm) bg-(--color-secondary) opacity-0 transition-opacity group-data-[state=active]/tab:opacity-100">
											{icon === 'plus' ? <PlusIcon className="size-3.5" /> : <PencilIcon className="size-3.5" />}
										</span>
									) : null}
								</TabsTrigger>
							)
						})}
					</TabsList>
				</Tabs>
			</div>

			{/* Body */}
			<div className="flex flex-1 overflow-auto">
				<div className="flex-1 overflow-auto px-4">
					{activeTab === 'characteristics' && <PropertyCharacteristics property={property} />}
					{activeTab === 'rent-terms' && <PropertyRentTerms property={property} />}
					{activeTab === 'publications' && <PropertyPublications property={property} />}
					{activeTab === 'documents' && (
						<div className="p-4">
							<PropertyDocumentsSection
								property={property}
								createOpen={selectTemplateOpen}
								onCreateOpenChange={setSelectTemplateOpen}
							/>
						</div>
					)}
					{activeTab === 'manager' && (() => {
						const subAccounts = subAccountsData ?? []
						const isSelf = property.managerId && me?.id === property.managerId
						const subAccount = property.managerId && !isSelf
							? subAccounts.find((sa) => sa.id === property.managerId)
							: undefined
						// The server already resolves the manager (PROPERTY_INCLUDE →
						// PropertyManagerResponse), so prefer it: deriving the name from
						// `me` plus the viewer's own sub-accounts only works for the
						// owner, and every employee would see "no manager assigned" on a
						// property that has one — with an Assign button that overwrites it.
						const displayName = (isSelf ? me?.name : subAccount?.fullName) ?? property.manager?.name
						// Сервер резолвит менеджера вместе с объектом, поэтому его
						// аватар подходит и для сотрудника, и для себя; `me` остаётся
						// запасным на случай, когда объект пришёл без менеджера.
						const avatarUrl = property.manager?.avatarUrl ?? (isSelf ? me?.avatar ?? undefined : undefined)
						return (
							<div className="px-4">
								<p className="py-4 text-base font-[500] leading-6 text-(--color-foreground)">
									{t('properties.card.managerTitle')}
								</p>
								{property.managerId && displayName ? (
									<div className="flex items-center gap-3">
										<Avatar className="size-8 rounded-full">
											{avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} className="rounded-full object-cover" />}
											<AvatarFallback className="text-xs rounded-full">
												{avatarInitials(displayName)}
											</AvatarFallback>
										</Avatar>
										<span className="text-sm text-(--color-foreground)">{displayName}</span>
										{isSelf && (
											<span className="text-xs text-(--color-muted-foreground)">{t('properties.card.assignManager.selfBadge')}</span>
										)}
									</div>
								) : (
									<div className="flex flex-col items-start gap-3 py-4">
										<p className="text-sm text-(--color-muted-foreground)">{t('properties.card.assignManager.empty')}</p>
										<Button variant="outline" size="sm" onClick={() => setAssignManagerOpen(true)}>
											{t('properties.card.assignManager.assign')}
										</Button>
									</div>
								)}
							</div>
						)
					})()}
				</div>

				{activeTab === 'characteristics' && (
					<div className="flex w-[527px] shrink-0 flex-col gap-4 overflow-auto p-4">
						{property.images.length > 0 && <PhotoGallery images={property.images} />}
						<PropertyPhotosSection propertyId={property.id} images={property.images} />
					</div>
				)}
				{activeTab === 'rent-terms' && (
					<div className="w-[524px] shrink-0 overflow-auto p-4">
						<PropertyRentSidebar property={property} />
					</div>
				)}
			</div>

			<EditPropertySheet property={property} open={editSheetOpen} onOpenChange={setEditSheetOpen} />
			<EditRentTermsDialog property={property} open={editRentTermsOpen} onOpenChange={setEditRentTermsOpen} />
			<AssignManagerDialog
				propertyId={property.id}
				currentManagerId={property.managerId ?? null}
				open={assignManagerOpen}
				onOpenChange={setAssignManagerOpen}
			/>

			<ConfirmDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				title={t('properties.deleteDialog.title')}
				description={t('properties.deleteDialog.description')}
				cancelLabel={t('common.cancel')}
				confirmLabel={t('properties.delete')}
				variant="destructive"
				onConfirm={async () => {
					try {
						await deleteProperty(property.id).unwrap()
						navigate({
							to: property.status === 'DRAFT'    ? '/properties/drafts' :
							    property.status === 'ARCHIVED' ? '/properties/archive' :
							                                     '/properties/registry',
						})
					} catch (err) {
						console.error('deleteProperty failed:', err)
					}
				}}
			/>

			<input
				ref={fileInputRef}
				type="file"
				accept="application/pdf,.doc,.docx,image/jpeg,image/png,image/webp,image/heic,image/heif"
				className="hidden"
				onChange={handlePropertyFilePicked}
			/>
		</div>
	)
}
